import { UserType } from '@metadevprofile/shared';
import { buttonVariants } from '@metadevprofile/ui/web';
import { BellIcon, CreditCardIcon, FolderIcon, LayoutDashboardIcon, UsersIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, NavLink, Outlet } from 'react-router';
import { selectUser } from '@/auth/stores/auth-slice';
import { CrispWeb } from '@/common/components/crisp-web';
import { Footer } from '@/common/components/sidebar/footer';
import { MainContentArea } from '@/common/components/sidebar/main-content-area';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarProvider,
} from '@/common/components/sidebar/sidebar';
import { ToggleSidebar } from '@/common/components/sidebar/toggle-sidebar';
import { getRouteByUserType } from '@/common/lib/get-route-by-user-type';
import { navLinkClass } from '@/common/lib/nav-link-class';
import { cn } from '@/common/lib/utils';
import { useAppSelector } from '@/common/redux/store';
import { institutionRoutes } from '@/institution/constants/routes';
import { useNotifications } from '@/notification/hooks/use-notifications';

export function InstitutionLayout() {
  const { t } = useTranslation('institution');
  const user = useAppSelector(selectUser);
  const { unreadCount: unreadNotifications } = useNotifications();

  if (!user) return null;

  if (user.type !== UserType.INSTITUTION) {
    return <Navigate to={getRouteByUserType(user)} replace />;
  }

  const sidebarItems = [
    {
      label: t('navigation.dashboard'),
      icon: LayoutDashboardIcon,
      path: institutionRoutes.dashboard,
    },
    {
      label: t('navigation.users'),
      icon: UsersIcon,
      path: institutionRoutes.users,
    },
    {
      label: t('navigation.groups'),
      icon: FolderIcon,
      path: institutionRoutes.groups,
    },
    {
      label: t('navigation.billing'),
      icon: CreditCardIcon,
      path: institutionRoutes.billing,
    },
  ];

  return (
    <SidebarProvider>
      <ToggleSidebar />
      <Sidebar className="bg-surface text-foreground p-2">
        <SidebarHeader>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <img src="/images/logo.png" alt="MetaDevProfile" style={{ width: 32, height: 32 }} />

              <div className="text font-semibold">MetaDevProfile</div>
            </div>
            <Link
              to={institutionRoutes.notifications}
              className={cn(buttonVariants({ variant: 'ghost', isIconOnly: true }), 'relative')}
            >
              {unreadNotifications > 0 && (
                <span className="absolute -top-1 -right-1 bg-danger text-danger-foreground rounded-full text-xs px-1 py-0.5 flex items-center justify-center font-bold">
                  {unreadNotifications}
                </span>
              )}
              <BellIcon className="size-4" />
            </Link>
          </div>
        </SidebarHeader>
        <SidebarContent className="flex-1 mt-2">
          <SidebarGroup className="px-0">
            <SidebarGroupLabel className="text-xs text-muted font-semibold">
              {t('navigation.general').toUpperCase()}
            </SidebarGroupLabel>
            <SidebarGroupContent className="px-0">
              <SidebarMenu className="gap-2 p-2">
                {sidebarItems.map((item) => (
                  <SidebarMenuItem key={item.path}>
                    <NavLink to={item.path} end className={({ isActive }) => navLinkClass(isActive)}>
                      <item.icon className="size-4" />
                      <span>{item.label}</span>
                    </NavLink>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <Footer />
        </SidebarFooter>
      </Sidebar>

      <MainContentArea>
        <Outlet />
        <CrispWeb />
      </MainContentArea>
    </SidebarProvider>
  );
}
